import { type FC, useEffect, useState } from 'react'

import { useSetFocus } from '@please/lrud'
import type { MoveEvent } from '@please/lrud/src/types'
import { scroller } from 'react-scroll'

import { getGroupsPositionColor } from '@sportworld/js-sdk/src/utils/soccerData'

import { orientation } from '@/constants/focus'
import { useDataWidgetTeamId } from '@/hooks'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'

import { TableRow } from './TableRow'
import { STANDINGS_GROUPS_COLUMNS } from '../config'
import { focusIds, STANDINGS_GROUPS_SCROLL_OFFSET } from '../constants'
import { renderTableHeader } from '../partials'
import {
  GroupItem,
  GroupItemHeader,
  GroupItemWrapper,
  GroupsWrapper,
} from '../styles'
import type { StandingsComponentProps } from '../types'

export const StandingsGroups: FC<
  StandingsComponentProps & { isPopup: boolean }
> = ({ isPopup, onClickTeam }) => {
  const { t } = useCommonTranslation()
  const { division: divisionPage, divisionPopup } = useStore(
    (state) => state.soccerData,
  )
  const { pagePopup, clearPagePopupOptions, setPagePopupFocusId } = useStore(
    (state) => state.popup,
  )

  const [enableAutoScroll, setEnableAutoScroll] = useState(true)
  const [focusedChildInGroup, setFocusedChildInGroup] = useState(0)
  const [activeGroupIndex, setActiveGroupIndex] = useState(0)

  const setFocus = useSetFocus()
  const teamId = useDataWidgetTeamId()

  const currentDivision = isPopup ? divisionPopup.division : divisionPage
  const focusIdPrefix = isPopup ? focusIds.pagePopup : ''
  const containerId = focusIdPrefix + focusIds.standingsGroupsWrapper
  const selectedTeamId = pagePopup?.pageOptions?.selectedTeamId

  /**
   * Ignore standing groups autoscroll
   * once user has moved scroll manually.
   * Idea – is to persist user group selection position.
   */
  useEffect(() => {
    const storedFocusId = pagePopup?.focusId
    const shouldRestoreFocus =
      isPopup && storedFocusId && selectedTeamId && currentDivision.length

    if (shouldRestoreFocus) {
      const groupIndex = currentDivision.findIndex((group, groupIndex) =>
        group.ranking.some((team, idx) => {
          const isSelectedTeam = team.contestantId === selectedTeamId
          const teamFocusId = focusIdPrefix + selectedTeamId + groupIndex + idx
          const isStoredFocusId = teamFocusId === storedFocusId

          return isSelectedTeam && isStoredFocusId
        }),
      )

      if (groupIndex !== -1) {
        handleScroll({ nextChildIndex: groupIndex } as MoveEvent)
        setFocus(storedFocusId)
      }
      setPagePopupFocusId('')
      clearPagePopupOptions()
    }
  }, [currentDivision.length])

  useEffect(() => {
    if (!selectedTeamId && teamId) {
      const index = currentDivision.findIndex((group) =>
        group.ranking.some((team) => team.contestantId === teamId),
      )
      index !== -1 &&
        enableAutoScroll &&
        handleScroll({ nextChildIndex: index } as MoveEvent)
    }
  }, [teamId])

  const getGroupsItemId = (index: number) => {
    return focusIdPrefix + `${focusIds.standingsGroupsItem}-${index}`
  }

  const handleScroll = ({ nextChildIndex }: MoveEvent) => {
    setActiveGroupIndex(nextChildIndex)
    const groupItemId = getGroupsItemId(nextChildIndex)

    scroller.scrollTo(groupItemId, {
      containerId,
      horizontal: true,
      smooth: true,
      duration: 0,
      offset: STANDINGS_GROUPS_SCROLL_OFFSET,
    })

    setEnableAutoScroll(false)
  }

  const handleClickInactiveGroup = (groupIndex: number) => {
    handleScroll({ nextChildIndex: groupIndex } as MoveEvent)
    setFocus(getGroupsItemId(groupIndex))
  }

  const handleSetDefaultFocusIndex = ({ nextChildIndex }: MoveEvent) => {
    setFocusedChildInGroup(nextChildIndex)
  }

  const renderGroups = () =>
    currentDivision.map((group, groupIndex) => {
      const groupItemId = getGroupsItemId(groupIndex)
      const isActive = groupIndex === activeGroupIndex
      const groupItemKey = focusIdPrefix + (group.groupId || group.groupName)

      return (
        <GroupItemWrapper key={groupItemKey}>
          <GroupItem
            defaultFocusChild={focusedChildInGroup}
            orientation={orientation.VERTICAL}
            focusId={groupItemId}
            id={groupItemId}
            onMove={handleSetDefaultFocusIndex}
            onClick={() => handleClickInactiveGroup(groupIndex)}
            $isActive={isActive}
          >
            <GroupItemHeader>{group.groupName}</GroupItemHeader>
            {renderTableHeader(STANDINGS_GROUPS_COLUMNS, t, groupIndex)}
            {group.ranking.map((team, idx) => {
              const highlightColor = getGroupsPositionColor(idx)
              const teamFocusId =
                focusIdPrefix + team.contestantId + groupIndex + idx
              return (
                <TableRow
                  focusId={teamFocusId}
                  key={teamFocusId}
                  columns={STANDINGS_GROUPS_COLUMNS}
                  team={team}
                  onClick={onClickTeam}
                  highlightColor={highlightColor}
                  firstColHighlighted
                  rowHighlighted={team.contestantId === teamId}
                />
              )
            })}
          </GroupItem>
        </GroupItemWrapper>
      )
    })

  return (
    <GroupsWrapper
      defaultFocusChild={activeGroupIndex}
      focusId={containerId}
      id={containerId}
      onMove={handleScroll}
    >
      {renderGroups()}
    </GroupsWrapper>
  )
}
